import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useProfile } from '../../contexts/ProfileContext';
import { uploadUserPicture, deleteProfilePicture } from '../../services/userSelfInfo.api';
import PropTypes from 'prop-types';

const ProfileAvatar = ({ user }) => {
  const { profilePictureUrl, loading: profilePictureLoading } = useProfile();
  const [avatarLoading, setAvatarLoading] = useState(profilePictureLoading);
  const [avatarError, setAvatarError] = useState(false);
  const avatarSrc = avatarError ? undefined : profilePictureUrl;
  const [avatarHover, setAvatarHover] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarInputKey, setAvatarInputKey] = useState(Date.now());
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedAvatarName, setSelectedAvatarName] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);

  React.useEffect(() => {
    if (profilePictureUrl && !profilePictureLoading) {
      setAvatarLoading(false);
    } else {
      setAvatarLoading(profilePictureLoading);
    }
  }, [profilePictureLoading, profilePictureUrl]);

  const handleAvatarModalClose = () => {
  setAvatarModalOpen(false);
  setSelectedAvatar(null);
  setSelectedAvatarName('');
  setAvatarError('');
  setAvatarInputKey(Date.now());
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (!validTypes.includes(file.type)) {
      setAvatarError('Only PNG, JPG, and WEBP formats are supported.');
      setSelectedAvatar(null);
      setSelectedAvatarName('');
      return;
    }
    if (file.size > maxSize) {
      setAvatarError('File size must be less than 2MB.');
      setSelectedAvatar(null);
      setSelectedAvatarName('');
      return;
    }
    setAvatarError('');
    setSelectedAvatar(file);
    setSelectedAvatarName(file.name);
  };

  const handleRemovePicture = async () => {
    try {
      await deleteProfilePicture();
      // Only reset error and state if delete succeeded
      setAvatarError(false);
      setSelectedAvatar(null);
      setSelectedAvatarName('');
      window.location.reload();
    } catch (error) {
      // Only set error message, do not reset avatarError or state
      setAvatarError('Failed to remove profile picture.');
      console.error(error);
    }
  };

  return (
    <>
      <div
        style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 16px auto', cursor: 'pointer' }}
        onMouseEnter={() => setAvatarHover(true)}
        onMouseLeave={() => setAvatarHover(false)}
        onClick={() => setAvatarModalOpen(true)}
        data-testid="profile-avatar-container"
      >
        <Avatar
          src={avatarLoading ? undefined : avatarSrc}
          sx={{
            bgcolor: avatarError || !profilePictureUrl ? '#1976d2' : '#fff',
            width: 80,
            height: 80,
            fontSize: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          slotProps={{
            img: {
              loading: 'lazy',
              style: { width: '100%', height: '100%', objectFit: 'cover' },
              onLoad: (e) => {
                setTimeout(() => setAvatarLoading(false), 1200);
              },
              onError: (e) => {
                setAvatarError(true);
                setAvatarLoading(false);
              }
            }
          }}
          data-testid="profile-avatar"
        >
          {(() => {
            if (avatarLoading) {
              return (
                <svg width="32" height="32" viewBox="0 0 24 24" style={{ margin: 'auto', display: 'block' }} data-testid="avatar-spinner">
                  <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="4" fill="none" strokeDasharray="60" strokeDashoffset="40">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              );
            }
            if (!profilePictureUrl || avatarError) {
              return <span data-testid="avatar-fallback-initial">{user.firstName[0]}</span>;
            }
            return null;
          })()}
        </Avatar>
        {avatarHover && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            color: '#fff',
            transition: 'background 0.2s',
            pointerEvents: 'none',
          }}>
            <CameraAltIcon fontSize="large" />
          </div>
        )}
      </div>
      <Modal open={!!avatarModalOpen} onClose={handleAvatarModalClose} data-testid="avatar-modal">
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          minWidth: 340,
          textAlign: 'center',
        }}>
          <Typography variant="h6" gutterBottom>Update Profile Picture</Typography>
          <Button variant="contained" component="label" sx={{ mb: 3, mt: 1 }}>
            Upload New Picture
            <input
              key={avatarInputKey}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              hidden
              onChange={handleAvatarUpload}
            />
          </Button>
          {selectedAvatarName && !avatarError && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {selectedAvatarName}
            </Typography>
          )}
          {avatarError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>{avatarError}</Typography>
          )}
          {selectedAvatar && !avatarError && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2, width: 200 }}
              disabled={avatarUploading}
              onClick={async () => {
                setAvatarUploading(true);
                try {
                  await uploadUserPicture(selectedAvatar);
                  setSelectedAvatar(null);
                  setSelectedAvatarName('');
                  setAvatarError('');
                  window.location.reload();
                } catch (error) {
                  setAvatarError('Failed to upload image.');
                  console.error(error);
                } finally {
                  setAvatarUploading(false);
                }
              }}
            >
              {avatarUploading ? 'Uploading...' : 'Save'}
            </Button>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button color="error" variant="outlined" sx={{ width: 200, mb: 1 }} onClick={handleRemovePicture}>
              Remove Picture
            </Button>
            <Button onClick={handleAvatarModalClose} variant="text" sx={{ width: 200 }}>Cancel</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

ProfileAvatar.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
  }).isRequired,
};

export default ProfileAvatar;
